import { ModuleLayout } from '@billy/design-system'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useIsContactsListEmpty } from '../../hooks/useIsContactsListEmpty'
import { ContactTypeContextProvider } from './contexts/contactTypeContext'
import { ContactsTable } from './elements/ContactsTable'
import { ContactsTableActions } from './elements/ContactsTableActions'
import { ContactsTableSideActions } from './elements/ContactsTableSideActions'
import { getContactPageProps } from './utils/getContactPageProps'

interface ContactsListProps {
  isCustomer?: boolean
  isSupplier?: boolean
}

export const ContactsList = ({ isCustomer = true, isSupplier = false }: ContactsListProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const isContactsListEmpty = useIsContactsListEmpty({ isCustomer, isSupplier })

  const { pageTitle, pageType } = useMemo(
    () => getContactPageProps({ isCustomer, isSupplier, t }),
    [isCustomer, isSupplier, t],
  )

  const { url: clientsEmptyUrl } = useOrganizationViewUrl(reactRoute.organization.clients.getListEmptyRoute())
  const { url: suppliersEmptyUrl } = useOrganizationViewUrl(reactRoute.organization.suppliers.getListEmptyRoute())

  useEffect(() => {
    track('XXX - Invoicing core - Contacts Page view', { filter: pageType })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType])

  if (isContactsListEmpty) {
    if (!clientsEmptyUrl || !suppliersEmptyUrl) {
      // This case should never happen, but we need to be type safe
      return null
    }

    return <Navigate to={isSupplier ? suppliersEmptyUrl : clientsEmptyUrl} replace />
  }

  return (
    <ContactTypeContextProvider isCustomer={isCustomer} isSupplier={isSupplier}>
      <ModuleLayout sideActions={<ContactsTableSideActions />} title={pageTitle}>
        <ContactsTableActions title={pageTitle} />
        <ContactsTable />
      </ModuleLayout>
    </ContactTypeContextProvider>
  )
}
